import { useEffect, useMemo, useState } from "react";
import { Activity, Calculator, ChartLine, Check, Utensils } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ScreenShell } from "../components/ScreenShell";
import { computePlan, planInputFromAnswers } from "../plan";
import type { OnboardingPlan } from "../plan";
import type { FlowAnswers } from "../types";

const STAGES = [
  { icon: Calculator, message: "Analyzing your metabolism…" },
  { icon: Activity, message: "Calculating your calorie target…" },
  { icon: ChartLine, message: "Balancing your macros…" },
  { icon: Utensils, message: "Personalizing your timeline…" },
] as const;

const READY_MESSAGE = "Your plan is ready";

const RING_MS = 4200;
const READY_HOLD_MS = 2600;
const TOTAL_MS = RING_MS + READY_HOLD_MS;

const RING_RADIUS = 96;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const PARTICLES = [
  { left: "6%", top: "18%", size: 5, delay: "0s", duration: "2.4s" },
  { left: "88%", top: "12%", size: 4, delay: "0.5s", duration: "2.9s" },
  { left: "94%", top: "58%", size: 6, delay: "1.1s", duration: "2.2s" },
  { left: "2%", top: "66%", size: 4, delay: "0.8s", duration: "3.1s" },
  { left: "22%", top: "92%", size: 5, delay: "1.6s", duration: "2.6s" },
  { left: "74%", top: "90%", size: 4, delay: "0.2s", duration: "3.4s" },
] as const;

interface BuildingPlanScreenProps {
  readonly answers: FlowAnswers;
  readonly onDone: () => void;
  readonly initialProgress?: number;
}

/** "12 Oct – 26 Oct" style range; null when the engine produced no goal dates. */
export function goalDateRangeLabel(plan: OnboardingPlan): string | null {
  if (plan.goalDateMin === undefined || plan.goalDateMax === undefined) return null;
  if (plan.weeksToGoal <= 0) return null;
  const format = (epochMs: number) =>
    new Date(epochMs).toLocaleDateString("en-US", { day: "numeric", month: "short" });
  const min = format(plan.goalDateMin);
  const max = format(plan.goalDateMax);
  return min === max ? min : `${min} – ${max}`;
}

/** Weekly rate label; null for maintenance-style plans with no meaningful rate. */
export function weeklyRateLabel(plan: OnboardingPlan): string | null {
  if (plan.weeklyRateKg <= 0) return null;
  return `≈${plan.weeklyRateKg} kg/week`;
}

function PlanReveal({ plan }: { readonly plan: OnboardingPlan }) {
  const rate = weeklyRateLabel(plan);
  const dates = goalDateRangeLabel(plan);
  const macros = [
    { label: "Protein", value: `${plan.proteinG} g` },
    { label: "Carbs", value: `${plan.carbsG} g` },
    { label: "Fat", value: `${plan.fatG} g` },
    { label: "Fibre", value: `${plan.fiberG} g` },
  ];
  return (
    <div
      data-testid="building-plan-reveal"
      className="ob-fade-swap flex w-full flex-col items-center gap-3"
    >
      <div className="flex w-full flex-col gap-3 rounded-2xl border border-border bg-surface p-4">
        <div className="flex items-baseline justify-center gap-1.5">
          <span
            data-testid="building-plan-kcal"
            className="text-3xl font-bold tabular-nums text-accent"
          >
            {Math.round(plan.dailyKcal).toLocaleString("en-US")}
          </span>
          <span className="text-sm font-medium text-text-muted">kcal / day</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {macros.map((macro) => (
            <div key={macro.label} className="flex flex-col items-center gap-0.5">
              <span className="text-sm font-bold tabular-nums text-text">{macro.value}</span>
              <span className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
                {macro.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      {(rate !== null || dates !== null) && (
        <p data-testid="building-plan-timeline" className="text-sm text-text-muted">
          {rate !== null && dates !== null ? `${rate} · Goal ${dates}` : (rate ?? `Goal ${dates}`)}
        </p>
      )}
    </div>
  );
}

/**
 * Full-screen plan-generation sequence: gradient progress ring with a counting
 * percent, orbiting dashed arcs, sequential stage chips, and floating particles.
 * At 100% the stage chips swap to the engine-computed headline numbers, then
 * the screen auto-advances after a short hold (~6.8s total).
 */
export function BuildingPlanScreen({
  answers,
  onDone,
  initialProgress = 0,
}: BuildingPlanScreenProps) {
  const [progress, setProgress] = useState(initialProgress);
  const plan = useMemo(() => computePlan(planInputFromAnswers(answers)), [answers]);

  useEffect(() => {
    console.log("[Frontend][BuildingPlanScreen] Plan generation sequence started");
    let rafId = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / RING_MS, 1);
      const eased = 1 - Math.pow(1 - t, 2);
      setProgress(Math.round(eased * 100));
      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    const doneTimer = setTimeout(() => {
      console.log("[Frontend][BuildingPlanScreen] Plan ready, advancing to results");
      onDone();
    }, TOTAL_MS);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(doneTimer);
    };
  }, [onDone]);

  const isReady = progress >= 100;
  const stageIndex = Math.min(STAGES.length - 1, Math.floor(progress / (100 / STAGES.length)));
  const message = isReady ? READY_MESSAGE : STAGES[stageIndex].message;
  const dashOffset = RING_CIRCUMFERENCE * (1 - progress / 100);

  return (
    <ScreenShell center>
      <div className="flex flex-col items-center gap-7 text-center">
        <h1 className="text-2xl font-bold text-text">Building your plan</h1>

        <div className="relative h-60 w-60">
          {PARTICLES.map((particle, index) => (
            <span
              key={index}
              aria-hidden="true"
              className="ob-particle absolute rounded-full bg-accent"
              style={{
                left: particle.left,
                top: particle.top,
                width: particle.size,
                height: particle.size,
                animationDelay: particle.delay,
                animationDuration: particle.duration,
              }}
            />
          ))}

          <svg viewBox="0 0 240 240" className="h-full w-full" role="img" aria-label={`Plan generation ${progress}% complete`}>
            <defs>
              <linearGradient id="ob-ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="var(--color-accent)" />
                <stop offset="100%" stopColor="var(--color-accent-dark)" />
              </linearGradient>
            </defs>

            <circle
              cx="120"
              cy="120"
              r="112"
              fill="none"
              stroke="var(--color-border)"
              strokeWidth="1.5"
              strokeDasharray="4 10"
              strokeLinecap="round"
              className="ob-orbit-spin"
            />

            <circle
              cx="120"
              cy="120"
              r="82"
              fill="none"
              stroke="var(--color-accent)"
              strokeOpacity="0.25"
              strokeWidth="2"
              strokeDasharray="30 70"
              strokeLinecap="round"
              className="ob-orbit-spin-reverse"
            />

            <circle
              cx="120"
              cy="120"
              r={RING_RADIUS}
              fill="none"
              stroke="var(--color-muted)"
              strokeWidth="10"
            />

            {/* Blurred duplicate of the progress arc for the pulsing glow. */}
            <circle
              cx="120"
              cy="120"
              r={RING_RADIUS}
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 120 120)"
              className="ob-ring-glow"
              style={{ filter: "blur(7px)" }}
            />

            <circle
              cx="120"
              cy="120"
              r={RING_RADIUS}
              fill="none"
              stroke="url(#ob-ring-gradient)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 120 120)"
            />
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-5xl font-bold tabular-nums text-text">{progress}</span>
            <span className="text-sm font-medium text-text-muted">%</span>
          </div>
        </div>

        {isReady ? (
          <PlanReveal plan={plan} />
        ) : (
          <div className="flex items-center gap-3" aria-hidden="true">
            {STAGES.map((stage, index) => {
              const StageIcon: LucideIcon = stage.icon;
              const state = index < stageIndex ? "done" : index === stageIndex ? "active" : "idle";
              return (
                <div
                  key={stage.message}
                  className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors duration-300 ${
                    state === "done"
                      ? "bg-accent-light text-accent"
                      : state === "active"
                        ? "ob-stage-pulse bg-accent text-white"
                        : "bg-muted text-text-muted"
                  }`}
                >
                  {state === "done" ? (
                    <Check size={20} strokeWidth={2.5} />
                  ) : (
                    <StageIcon size={20} strokeWidth={2} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p key={message} className="ob-fade-swap text-sm text-text-muted" aria-live="polite">
          {message}
        </p>
      </div>
    </ScreenShell>
  );
}
